import React, { useState, useRef, useEffect } from 'react';

/**
 * VoiceCapture — the Admin-panel voice-sample capture UX for XTTS cloning.
 *
 * Three ways to provide a reference voice, all feeding the same uploadAsset(id,'voice',file):
 *   1. Upload any audio format (m4a/mp3/wav/ogg/webm…) — the GPU worker transcodes
 *      to clean 22kHz mono PCM, so any input works.
 *   2. Record in-browser via MediaRecorder (mic → preview → upload).
 *   3. Read a guided, phonetically-rich script while recording — this gives XTTS
 *      full phoneme coverage, which is the single biggest driver of clone quality.
 *
 * Props:
 *   personaId   — the persona to attach the sample to
 *   voiceUrl    — existing sample URL (if any) for playback
 *   canManage   — RBAC gate
 *   uploadAsset — (id, kind, file) => Promise, from PersonaContext
 *   deleteAsset — (id, kind) => Promise
 *   hasVoiceKey — whether a sample already exists (to show Delete)
 */

// A phonetically-rich passage (~45-60s read) that covers the English phoneme set
// well — pangrams + varied vowels/consonants. Reading this produces a far better
// clone than a random short clip.
const GUIDED_SCRIPT = [
  "The quick brown fox jumps over the lazy dog while five wizards gaze proudly.",
  "She sells seashells by the seashore, and the shells she sells are surely seashells.",
  "Bright blue skies brought a gentle breeze through the tall, swaying summer trees.",
  "I thought a thousand thoughts, though few were worth the thorough things we sought.",
  "Please call me around eleven; we'll chat about the project, the budget, and the schedule.",
  "Joyful children chased the bouncing ball, laughing loudly as the autumn leaves fell.",
];

const label = { display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: '#333' };

export default function VoiceCapture({ personaId, voiceUrl, canManage, uploadAsset, deleteAsset, hasVoiceKey }) {
  const [mode, setMode] = useState('upload');     // 'upload' | 'record'
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [err, setErr] = useState('');
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startRec = async () => {
    setErr(''); setPreviewUrl(null); setRecordedBlob(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setRecordedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } catch (e) {
      setErr('Microphone access denied or unavailable. Use Upload instead.');
    }
  };

  const stopRec = () => {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const uploadRecording = () => {
    if (!recordedBlob) return;
    // Name it .webm; the worker transcodes to PCM wav regardless of container.
    const file = new File([recordedBlob], 'recording.webm', { type: recordedBlob.type || 'audio/webm' });
    uploadAsset(personaId, 'voice', file);
    setPreviewUrl(null); setRecordedBlob(null); setElapsed(0);
  };

  const mm = Math.floor(elapsed / 60), ss = elapsed % 60;
  const durColor = elapsed < 20 ? '#b00020' : elapsed > 90 ? '#b7791f' : '#28a745';

  return (
    <div>
      <label style={label}>Voice Sample (to clone)</label>

      {/* Existing sample playback */}
      {voiceUrl
        ? <audio src={voiceUrl} controls style={{ width: '100%', height: '32px', marginBottom: '6px' }} />
        : <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>none yet</div>}

      {canManage && (
        <>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <button onClick={() => setMode('upload')} style={tab(mode === 'upload')}>⬆️ Upload</button>
            <button onClick={() => setMode('record')} style={tab(mode === 'record')}>🎙️ Record</button>
          </div>

          {mode === 'upload' && (
            <div>
              <input type="file" accept="audio/*" style={{ fontSize: '11px' }}
                onChange={e => uploadAsset(personaId, 'voice', e.target.files && e.target.files[0])} />
              <div style={{ fontSize: '10px', color: '#718096', marginTop: '4px' }}>
                Any format (m4a, mp3, wav, ogg…). Best results: 30–90s of clean speech, quiet room.
              </div>
            </div>
          )}

          {mode === 'record' && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px', background: '#fbfcfe' }}>
              {/* Guided script */}
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#718096', marginBottom: '4px' }}>
                Read this aloud for the best clone (covers all key sounds):
              </div>
              <div style={{ fontSize: '12px', color: '#1a202c', lineHeight: 1.5, marginBottom: '10px', maxHeight: '120px', overflow: 'auto', padding: '6px', background: '#fff', borderRadius: '4px', border: '1px solid #edf2f7' }}>
                {GUIDED_SCRIPT.map((line, i) => <div key={i} style={{ marginBottom: '4px' }}>{i + 1}. {line}</div>)}
              </div>

              {/* Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {!recording && (
                  <button onClick={startRec} style={{ padding: '7px 14px', border: 'none', borderRadius: '4px', background: '#dc3545', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>
                    ● Record
                  </button>
                )}
                {recording && (
                  <button onClick={stopRec} style={{ padding: '7px 14px', border: 'none', borderRadius: '4px', background: '#0073bb', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>
                    ■ Stop
                  </button>
                )}
                {(recording || elapsed > 0) && (
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: durColor }}>
                    {mm}:{String(ss).padStart(2, '0')}
                    {recording && <span style={{ color: '#dc3545' }}> ●</span>}
                  </span>
                )}
                {!recording && elapsed > 0 && elapsed < 20 && (
                  <span style={{ fontSize: '10px', color: '#b00020' }}>too short — aim for 30s+</span>
                )}
              </div>

              {/* Preview + upload */}
              {previewUrl && !recording && (
                <div style={{ marginTop: '10px' }}>
                  <audio src={previewUrl} controls style={{ width: '100%', height: '32px' }} />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <button onClick={uploadRecording} style={{ padding: '6px 14px', border: 'none', borderRadius: '4px', background: '#28a745', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>
                      Use this recording
                    </button>
                    <button onClick={() => { setPreviewUrl(null); setRecordedBlob(null); setElapsed(0); }} style={{ padding: '6px 14px', border: '1px solid #cbd5e0', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>
                      Discard
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {err && <div style={{ fontSize: '11px', color: '#b00020', marginTop: '6px' }}>{err}</div>}

          {hasVoiceKey && (
            <button onClick={() => deleteAsset(personaId, 'voice')} style={{ display: 'block', marginTop: '6px', fontSize: '11px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}>
              Delete voice
            </button>
          )}
        </>
      )}
    </div>
  );
}

function tab(active) {
  return {
    padding: '5px 12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer',
    borderRadius: '4px', border: active ? '2px solid #0073bb' : '1px solid #cbd5e0',
    background: active ? '#eef6fc' : '#fff', color: active ? '#0073bb' : '#4a5568',
  };
}
